import {
    createReportConfigurationExcelLayoutTemplate,
    deleteReportConfigurationExcelLayoutTemplate,
    getReportConfigurationExcelLayoutTemplates,
    updateReportConfigurationExcelLayoutTemplate,
} from "../../../../service/httpsCalls";
import type {
    DisplayAs,
    ReportConfigurationExcelLayoutTemplateDto,
    SaveReportConfigurationExcelLayoutTemplatePayload,
} from "../types";

const templateCache = new Map<string, ReportConfigurationExcelLayoutTemplateDto[]>();
const templateRequests = new Map<string, Promise<ReportConfigurationExcelLayoutTemplateDto[]>>();

function templateCacheKey(dataModelId: number, dataModelColumnId: number, displayAs?: DisplayAs | string, reportId?: number) {
    return `${dataModelId}:${dataModelColumnId}:${displayAs || ""}:report:${reportId || ""}`;
}

export async function loadTemplates(dataModelId: number, dataModelColumnId: number, displayAs?: DisplayAs | string, reportId?: number) {
    const cacheKey = templateCacheKey(dataModelId, dataModelColumnId, displayAs, reportId);
    const cached = templateCache.get(cacheKey);
    if (cached) return cached;

    const currentRequest = templateRequests.get(cacheKey);
    if (currentRequest) return currentRequest;

    const request = getReportConfigurationExcelLayoutTemplates(dataModelId, dataModelColumnId, displayAs, { reportId, scope: "report" })
        .then((response) => {
            const templates = Array.isArray(response) ? response : [];
            templateCache.set(cacheKey, templates);
            return templates;
        })
        .finally(() => {
            templateRequests.delete(cacheKey);
        });

    templateRequests.set(cacheKey, request);
    return request;
}

export const loadScopedTemplates = (
    dataModelId: number,
    dataModelColumnId: number,
    displayAs?: DisplayAs | string,
    options?: { reportId?: number | null; scope?: "report" | "global" },
) => getReportConfigurationExcelLayoutTemplates(dataModelId, dataModelColumnId, displayAs, options) as Promise<ReportConfigurationExcelLayoutTemplateDto[]>;

export const createTemplate = (payload: SaveReportConfigurationExcelLayoutTemplatePayload) =>
    createReportConfigurationExcelLayoutTemplate(payload) as Promise<ReportConfigurationExcelLayoutTemplateDto>;

export const updateTemplate = (templateId: number, payload: SaveReportConfigurationExcelLayoutTemplatePayload) =>
    updateReportConfigurationExcelLayoutTemplate(templateId, payload) as Promise<ReportConfigurationExcelLayoutTemplateDto>;

export const deleteTemplate = (templateId: number) =>
    deleteReportConfigurationExcelLayoutTemplate(templateId);

export function clearExcelLayoutTemplateCache(dataModelId?: number, dataModelColumnId?: number, displayAs?: DisplayAs | string) {
    if (!dataModelId || !dataModelColumnId) {
        templateCache.clear();
        return;
    }

    if (displayAs) {
        Array.from(templateCache.keys())
            .filter((key) => key.startsWith(`${dataModelId}:${dataModelColumnId}:${displayAs}:`))
            .forEach((key) => templateCache.delete(key));
        return;
    }

    Array.from(templateCache.keys())
        .filter((key) => key.startsWith(`${dataModelId}:${dataModelColumnId}:`))
        .forEach((key) => templateCache.delete(key));
}
